<template>
  <div class="row">
    <div class="col-12 d-flex justify-content-between align-items-center mb-3">
      <h2>{{ $t('sepa_direct_debits.headline') }}</h2>
      <router-link class="btn btn-outline-secondary" :to="{name: ROUTES.DUNNING.INDEX}">
        {{ $t('sepa_direct_debits.back_to_dunning') }}
      </router-link>
    </div>

    <div class="col-12">
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <button class="nav-link" :class="{active: activeTab === 'settled'}" type="button" @click="setActiveTab('settled')">
            {{ $t('sepa_direct_debits.settled_bookings') }}
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link" :class="{active: activeTab === 'submitted'}" type="button" @click="setActiveTab('submitted')">
            {{ $t('sepa_direct_debits.submitted_bookings') }}
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link" :class="{active: activeTab === 'bookings'}" type="button" @click="setActiveTab('bookings')">
            {{ $t('sepa_direct_debits.open_bookings') }}
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link" :class="{active: activeTab === 'exports'}" type="button" @click="setActiveTab('exports')">
            {{ $t('sepa_direct_debits.exports') }}
          </button>
        </li>
      </ul>
    </div>

    <div class="col-12" v-if="isBookingTab">
      <div class="d-flex justify-content-between align-items-center mb-3" v-if="activeTab === 'bookings'">
        <div class="text-muted">
          {{ $t('sepa_direct_debits.selected_count', {count: selectedPaymentBookings.length}) }}
        </div>
        <button class="btn btn-primary" type="button" :disabled="!canCreateExport || sepaDirectDebitExportStore.loading" @click="createBatchExport">
          {{ $t('sepa_direct_debits.create_export') }}
        </button>
      </div>

      <div class="alert alert-warning" v-if="activeTab === 'bookings' && mixedCreditorAccounts">
        {{ $t('sepa_direct_debits.mixed_creditor_accounts') }}
      </div>

      <DataTable stripedRows :lazy="true"
                 :paginator="true"
                 :rows="paymentBookingStore.serverOptions.rows"
                 :totalRecords="paymentBookingStore.itemLength"
                 dataKey="id"
                 v-model:selection="selectedPaymentBookings"
                 :value="paymentBookingStore.paymentBookings"
                 :loading="paymentBookingStore.loading"
                 paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                 :rowsPerPageOptions="[10,20,50]"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                 @page="onPaymentBookingsPage($event)"
                 responsiveLayout="scroll">
        <TableColumn v-if="activeTab === 'bookings'" selectionMode="multiple" headerStyle="width: 3rem"></TableColumn>
        <TableColumn field="requested_on" :header="$t('sepa_direct_debits.requested_on')">
          <template #body="{data}">{{ formatDate(data.requested_on, 'short2') }}</template>
        </TableColumn>
        <TableColumn field="client.kdnr" :header="$t('sepa_direct_debits.customer_number')">
          <template #body="{data}">{{ data.client?.kdnr || '-' }}</template>
        </TableColumn>
        <TableColumn field="client.name" :header="$t('sepa_direct_debits.customer')">
          <template #body="{data}">{{ data.client?.name || '-' }}</template>
        </TableColumn>
        <TableColumn field="invoice_number" :header="$t('sepa_direct_debits.invoice')">
          <template #body="{data}">
            <router-link :to="{name: ROUTES.DUNNING.EDIT, params: {invoice_id: data.invoice_id}}">
              {{ data.invoice_number || data.invoice_id }}
            </router-link>
          </template>
        </TableColumn>
        <TableColumn field="amount" :header="$t('sepa_direct_debits.amount')" headerClass="text-end" bodyClass="text-end">
          <template #body="{data}">
            <div class="text-end">{{ formatCurrency(data.amount) }}</div>
          </template>
        </TableColumn>
        <TableColumn field="sequence_type" :header="$t('sepa_direct_debits.sequence_type')"></TableColumn>
        <TableColumn field="remittance_information" :header="$t('sepa_direct_debits.remittance_information')"></TableColumn>
        <TableColumn v-if="activeTab === 'settled'" field="invoice_date_of_payment" :header="$t('sepa_direct_debits.paid_on')">
          <template #body="{data}">{{ data.invoice_date_of_payment ? formatDate(data.invoice_date_of_payment, 'short2') : '-' }}</template>
        </TableColumn>
        <TableColumn v-if="activeTab !== 'settled'" field="active_export_id" :header="$t('sepa_direct_debits.active_export')">
          <template #body="{data}">
            <router-link v-if="data.active_export_id"
                         :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBIT_EXPORT, params: {id: data.active_export_id}}">
              {{ exportLabel(data.active_export_id) }}
            </router-link>
            <span v-else>-</span>
          </template>
        </TableColumn>
      </DataTable>
    </div>

    <div class="col-12" v-if="activeTab === 'exports'">
      <DataTable stripedRows :lazy="true"
                 :paginator="true"
                 :rows="sepaDirectDebitExportStore.serverOptions.rows"
                 :totalRecords="sepaDirectDebitExportStore.itemLength"
                 dataKey="id"
                 :value="sepaDirectDebitExportStore.exports"
                 :loading="sepaDirectDebitExportStore.loading"
                 paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                 :rowsPerPageOptions="[10,20,50]"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                 @page="onExportsPage($event)"
                 responsiveLayout="scroll">
        <TableColumn field="id" :header="$t('sepa_direct_debits.export')">
          <template #body="{data}">
            <router-link :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBIT_EXPORT, params: {id: data.id}}">
              {{ exportLabel(data.id) }}
            </router-link>
          </template>
        </TableColumn>
        <TableColumn field="state" :header="$t('sepa_direct_debits.state')"></TableColumn>
        <TableColumn field="created_at" :header="$t('sepa_direct_debits.created_at')">
          <template #body="{data}">{{ formatDate(data.created_at, 'short2') }}</template>
        </TableColumn>
        <TableColumn field="booking_count" :header="$t('sepa_direct_debits.booking_count')"></TableColumn>
        <TableColumn field="control_sum" :header="$t('sepa_direct_debits.control_sum')" headerClass="text-end" bodyClass="text-end">
          <template #body="{data}">
            <div class="text-end">{{ formatCurrency(data.control_sum) }}</div>
          </template>
        </TableColumn>
        <TableColumn field="creditor_account_id" :header="$t('sepa_direct_debits.creditor_account')"></TableColumn>
        <TableColumn field="message_id" :header="$t('sepa_direct_debits.message_id')"></TableColumn>
        <TableColumn :header="$t('accounts.operations')">
          <template #body="{data}">
            <div class="d-flex flex-wrap gap-2">
              <router-link class="btn btn-sm btn-outline-secondary"
                           :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBIT_EXPORT, params: {id: data.id}}">
                {{ $t('sepa_direct_debits.show_export') }}
              </router-link>
              <button v-if="data.xml_url" class="btn btn-sm btn-outline-primary" type="button" @click="downloadXml(data.id)">
                {{ $t('sepa_direct_debits.download_xml') }}
              </button>
              <button v-if="data.state === 'generated'" class="btn btn-sm btn-outline-success" type="button" @click="submitExport(data.id)">
                {{ $t('sepa_direct_debits.mark_submitted') }}
              </button>
              <button v-if="data.state === 'generated'" class="btn btn-sm btn-outline-danger" type="button" @click="discardExport(data.id)">
                {{ $t('sepa_direct_debits.discard') }}
              </button>
            </div>
          </template>
        </TableColumn>
      </DataTable>
    </div>
  </div>
</template>

<script>
import {usePaymentBookingStore} from "@/stores/payment_bookings";
import {useSepaDirectDebitExportStore} from "@/stores/sepa_direct_debit_exports";
import {Helpers} from "@/helpers";
import {ROUTES} from "@/scripts/constants";

export default {
  name: 'SepaDirectDebits',
  setup() {
    return {
      paymentBookingStore: usePaymentBookingStore(),
      sepaDirectDebitExportStore: useSepaDirectDebitExportStore(),
    }
  },
  data() {
    return {
      activeTab: 'bookings',
      selectedPaymentBookings: [],
    }
  },
  computed: {
    ROUTES() {
      return ROUTES
    },
    selectedCreditorAccountIds() {
      return [...new Set(this.selectedPaymentBookings.map((booking) => booking.creditor_account_id))]
    },
    mixedCreditorAccounts() {
      return this.selectedCreditorAccountIds.length > 1
    },
    canCreateExport() {
      return this.activeTab === 'bookings' && this.selectedPaymentBookings.length > 0 && !this.mixedCreditorAccounts
    },
    isBookingTab() {
      return ['bookings', 'submitted', 'settled'].includes(this.activeTab)
    },
  },
  created() {
    if (this.$route.query.export_id) {
      this.$router.replace({
        name: ROUTES.DUNNING.SEPA_DIRECT_DEBIT_EXPORT,
        params: {id: this.$route.query.export_id},
      })
      return
    }
    if (['exports', 'submitted', 'settled'].includes(this.$route.query.tab)) {
      this.activeTab = this.$route.query.tab
    }
    this.setTitle(this.$t('sepa_direct_debits.headline'))
    this.loadActiveBookingTab()
    this.loadExports()
  },
  methods: {
    setActiveTab(tab) {
      this.activeTab = tab
      this.selectedPaymentBookings = []
      this.paymentBookingStore.serverOptions = {
        ...this.paymentBookingStore.serverOptions,
        page: 0,
      }
      this.$router.replace({
        name: ROUTES.DUNNING.SEPA_DIRECT_DEBITS,
        query: tab === 'bookings' ? {} : {tab},
      })
      if (this.isBookingTab) {
        this.loadActiveBookingTab()
      }
    },
    exportLabel(exportId) {
      return this.$t('sepa_direct_debits.export_tab', {id: exportId})
    },
    loadActiveBookingTab() {
      const params = {instrument: 'sepa_direct_debit'}
      if (this.activeTab === 'bookings') {
        params.state = 'open'
      } else if (this.activeTab === 'submitted') {
        params.state = 'submitted'
        params.invoice_unpaid = true
      } else if (this.activeTab === 'settled') {
        params.state = 'settled'
        params.invoice_paid_within_days = 30
      } else {
        return
      }
      return this.paymentBookingStore.index(params)
    },
    loadExports() {
      return this.sepaDirectDebitExportStore.index()
    },
    onPaymentBookingsPage(event) {
      this.paymentBookingStore.serverOptions = event
      this.loadActiveBookingTab()
    },
    onExportsPage(event) {
      this.sepaDirectDebitExportStore.serverOptions = event
      this.loadExports()
    },
    createBatchExport() {
      const ids = this.selectedPaymentBookings.map((booking) => booking.id)
      this.sepaDirectDebitExportStore.createFromPaymentBookings(ids).then((sepaExport) => {
        Helpers.emitter.emit('show-toast', {severity: 'success', summary: this.$t('sepa_direct_debits.export_created')})
        this.selectedPaymentBookings = []
        this.$router.push({
          name: ROUTES.DUNNING.SEPA_DIRECT_DEBIT_EXPORT,
          params: {id: sepaExport.id},
        })
      }).catch((error) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'error',
          summary: error.response?.data?.message || this.$t('sepa_direct_debits.export_failed')
        })
      })
    },
    downloadXml(exportId) {
      return this.sepaDirectDebitExportStore.downloadXml(exportId)
    },
    submitExport(exportId) {
      if (!window.confirm(this.$t('sepa_direct_debits.submit_confirm'))) {
        return
      }
      this.sepaDirectDebitExportStore.submit(exportId).then(() => {
        Helpers.emitter.emit('show-toast', {severity: 'success', summary: this.$t('sepa_direct_debits.submit_success')})
        this.loadActiveBookingTab()
        this.loadExports()
      }).catch((error) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'error',
          summary: error.response?.data?.message || this.$t('sepa_direct_debits.submit_failed')
        })
      })
    },
    discardExport(exportId) {
      if (!window.confirm(this.$t('sepa_direct_debits.discard_confirm'))) {
        return
      }
      this.sepaDirectDebitExportStore.discard(exportId).then(() => {
        Helpers.emitter.emit('show-toast', {severity: 'success', summary: this.$t('sepa_direct_debits.discard_success')})
        this.loadActiveBookingTab()
        this.loadExports()
      }).catch((error) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'error',
          summary: error.response?.data?.message || this.$t('sepa_direct_debits.discard_failed')
        })
      })
    },
  },
}
</script>
